import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconFiles,
  IconCalendarEvent,
  IconChartHistogram,
  IconBox,
  IconShieldCheck,
} from '@tabler/icons-react';
import { C, RADIUS, PAGE_TITLE, PAGE_SUB } from '../theme';
import Logo from '../components/Logo';
import OrgSwitcher from '../components/OrgSwitcher';
import { GearIcon, MicIcon } from '../components/InlineIcons';
import { useVoice } from '../components/VoiceModal';
import { useDraftPO } from '../components/DraftPOFlow';
import { useOrg } from '../components/OrgContext';
import { useT, useLang } from '../i18n';
import { supabase } from '../supabase';
import { currencySymbol } from '../lib/money';
import { loadHomeCaseTotals } from '../lib/homeCaseTotals';
import { loadInventoryTile } from '../lib/homeInventoryTile';
import { swr } from '../lib/dataCache';
import { apiFetch } from '../lib/api';

// Static parts of each launcher tile. Subtitle/badge are derived from live
// counts in the component so the numbers always match the real pages.
const TILES = [
  { key: 'cases', to: '/cases', label: 'Cases', Icon: IconFiles },
  { key: 'schedule', to: '/schedule', label: 'Schedule', Icon: IconCalendarEvent },
  { key: 'reports', to: '/reports', label: 'Reports', Icon: IconChartHistogram },
  { key: 'inventory', to: '/inventory', label: 'Inventory', Icon: IconBox },
];

let _activeCurrency = null;
export const setActiveCurrency = (c) => { _activeCurrency = c; };
const fmtMoney = (n) => currencySymbol(_activeCurrency) + Math.round(n).toLocaleString('en-GB');

export default function Home() {
  const navigate = useNavigate();
  const tr = useT();
  const { lang, setLang } = useLang();
  const { openVoice, voiceActive } = useVoice();
  // Count of auto-prepared draft POs awaiting approval, shared from the
  // draft-PO provider so the Inventory tile, nav badge and banner all agree.
  // `version` bumps when a PO is sent, so re-fetch counts in step with it.
  const { pendingCount, version } = useDraftPO();
  // Org derived from the logged-in user (replaces the old hardcoded constant).
  const { orgId: ORG_ID, currency: _orgCurrency, capabilities, can } = useOrg();
  // Money on this screen renders in the org's own currency, not a hardcoded euro.
  setActiveCurrency(_orgCurrency);

  // Live attention counts (null = still loading; show nothing rather than a
  // fake 0). Each mirrors the logic the matching page already uses.
  const [casesNeedReview, setCasesNeedReview] = useState(null);
  const [overdueCount, setOverdueCount] = useState(null);
  const [runningLow, setRunningLow] = useState(null);
  // Read-only "value created" signals: money Floxir caught + bills it checked.
  const [caughtAmount, setCaughtAmount] = useState(null);
  const [billsChecked, setBillsChecked] = useState(null);
  // Real figures for the tile subtitles — a category label ("What you have") tells
  // nobody anything they didn't already know from the tile's name.
  const [casesToCheck, setCasesToCheck] = useState(null);
  const [stockItems, setStockItems] = useState(null);
  const [upcoming, setUpcoming] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Show what was last read for THIS user and company at once (lib/dataCache.js, memory only), then replace it
    // with a fresh read. Any action that changes data drops the remembered copy, so it never shows a stale state.
    swr({
      orgId: ORG_ID,
      name: 'home',
      onData: (data) => { if (!cancelled) apply(data); },
      load: async () => {
      const [caseTotals, deliveriesRes, inventoryTile] = await Promise.all([
        // The four numbers that need ALL cases (waiting on a decision, money caught, bills checked, cases to
        // check), computed in the database in one call instead of downloading every case. If that call is not
        // available the same numbers are computed the old way (see lib/homeCaseTotals.js).
        loadHomeCaseTotals({ apiFetch, supabase, orgId: ORG_ID }),
        // Overdue deliveries — ASK SCHEDULE, don't re-derive. This tile is a preview
        // of the Schedule page, so the only correct number is the one that page shows.
        // The local query it replaces filtered on status ('sent','partially_received')
        // while every real order sits at 'submitted', and it never checked whether the
        // goods had actually turned up: Schedule said 8 deliveries were late, this tile
        // said none, on the same data, on the same screen.
        // Only the two counts the tiles show come back (the same rules as the page, computed on the
        // server without loading the whole agenda).
        apiFetch('/api/schedule/summary')
          .then((r) => r.json())
          .catch(() => null),
        // "Running low": the company's COMPLETE stock per product PER SITE against its reorder rules, computed
        // on the server in one call. A reorder rule is a threshold for ONE site, and reading it against another
        // site's stock is what made this tile disagree with the Inventory page it opens, so the warehouse-aware
        // rule in lib/lowStock.js is the one used (see lib/homeInventoryTile.js for the fallback).
        loadInventoryTile({ apiFetch, supabase, orgId: ORG_ID }),
      ]);
      // Only a COMPLETE read is remembered: a tile that failed to load must not be shown again from memory.
      const complete = caseTotals.needsReview != null && caseTotals.caught != null && !!(deliveriesRes && deliveriesRes.ok) && inventoryTile.runningLow != null;
      return { value: { caseTotals, deliveriesRes, inventoryTile }, cacheable: complete };
      },
    });
    function apply({ caseTotals, deliveriesRes, inventoryTile }) {

      // Cases
      setCasesNeedReview(caseTotals.needsReview);

      // Overdue deliveries — straight off the Schedule agenda, so the tile and the
      // page it opens can never disagree. null (not 0) when the call failed, so a
      // hiccup shows nothing rather than a confident "all on time".
      setOverdueCount(
        deliveriesRes && deliveriesRes.ok
          ? deliveriesRes.overdue_deliveries
          : null
      );
      // Everything on the forward agenda, for the Schedule tile's subtitle.
      setUpcoming(deliveriesRes && deliveriesRes.ok ? deliveriesRes.upcoming : null);

      // Running low — the shared warehouse-aware rule from lib/lowStock, the same one the Inventory hub card
      // uses, which is the only way the tile and the page can be guaranteed to say the same thing.
      if (inventoryTile.runningLow == null) {
        setRunningLow(null);
      } else {
        setRunningLow(inventoryTile.runningLow);
        setStockItems(inventoryTile.stockItems);
      }

      // Value created: money caught (sum of qualifying discrepancies) and the
      // number of bills Floxir has checked. Hidden when zero — never a fake 0.
      // Bills, not cases: counting every case once told Demo Decor that 55 bills had been checked when only 45
      // bills exist (the other 10 cases are orders still waiting for one); "cases to check" is what is
      // genuinely still open, not the total ever processed. Both definitions live in lib/homeCaseTotals.js
      // and in public.home_case_totals, and are proven equal by tests.
      if (caseTotals.caught == null) {
        setCaughtAmount(null);
        setBillsChecked(null);
      } else {
        setCaughtAmount(caseTotals.caught);
        setBillsChecked(caseTotals.billsChecked);
        setCasesToCheck(caseTotals.casesToCheck);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [version, ORG_ID]);

  // Build each tile's live subtitle + badge. A zero count renders a calm,
  // neutral subtitle and no badge — never a fake "0".
  // Every subtitle is a live figure where one exists. A count that hasn't loaded shows
  // the calm description rather than a fake 0.
  const tiles = TILES.filter((t) => t.key !== 'reports' || can('reports.financial')).map((t) => {
    if (t.key === 'cases') {
      return {
        ...t,
        subtitle: casesToCheck != null ? tr('home.tile.cases.count', { n: casesToCheck }) : tr('home.tile.cases.sub'),
        subtitleColor: C.textFaint,
        badge: casesNeedReview > 0 ? { text: tr('home.tile.cases.badge', { n: casesNeedReview }) } : null,
      };
    }
    if (t.key === 'schedule') {
      return {
        ...t,
        subtitle: overdueCount > 0 ? tr('home.tile.schedule.overdue', { n: overdueCount })
          : upcoming != null ? tr('home.tile.schedule.count', { n: upcoming })
          : tr('home.tile.schedule.sub'),
        subtitleColor: overdueCount > 0 ? C.red : C.textFaint,
      };
    }
    if (t.key === 'inventory') {
      return {
        ...t,
        subtitle: runningLow > 0 ? tr('home.tile.inventory.low', { n: runningLow })
          : stockItems != null ? tr('home.tile.inventory.count', { n: stockItems })
          : tr('home.tile.inventory.sub'),
        subtitleColor: runningLow > 0 ? C.red : C.textFaint,
        badge:
          pendingCount > 0
            ? { text: tr('nav.readyToOrder', { n: pendingCount }), kind: 'orange' }
            : null,
      };
    }
    // Reports. "Commission & analytics" is AGENT wording — a distributor earns no
    // commission, so it described a feature this workspace does not have.
    return {
      ...t,
      subtitle: caughtAmount > 0 ? `${fmtMoney(caughtAmount)} caught so far`
        : tr(capabilities?.agent ? 'home.tile.reports.sub.agent' : 'home.tile.reports.sub'),
      subtitleColor: C.textFaint,
    };
  });

  // Greeting attention sum: cases to decide + overdue items + items running
  // low. Counts still loading count as 0 until they resolve.
  const loaded =
    casesNeedReview != null && overdueCount != null && runningLow != null;
  const attention =
    (casesNeedReview || 0) + (overdueCount || 0) + (runningLow || 0);
  const greetingSub = !loaded
    ? tr('home.sub.loading')
    : attention === 0
    ? tr('home.sub.caughtUp')
    : attention === 1
    ? tr('home.sub.attentionOne')
    : tr('home.sub.attentionMany', { n: attention });

  return (
    <div
      style={{
        background: C.bg,
        padding: '28px 32px 36px',
        minHeight: '100vh',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 40,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Logo size={40} wordmarkSize={27} />
          <OrgSwitcher />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LangSwitcher lang={lang} setLang={setLang} />
          <button
            type="button"
            aria-label={tr('home.settings')}
            onClick={() => navigate('/settings')}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: C.card,
              border: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <GearIcon size={20} color={C.textMuted} />
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 32 }}>
        <div style={PAGE_TITLE}>{tr('home.greeting')}</div>
        <div style={{ ...PAGE_SUB, marginBottom: 0 }}>{greetingSub}</div>
      </div>

      <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {tiles.map(({ key, to, label, subtitle, subtitleColor, Icon, badge }) => (
            <button
              key={key}
              type="button"
              onClick={() => navigate(to)}
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: RADIUS.lg,
                padding: 26,
                // Sized by a floor, not a ratio — see HubCard.
                minHeight: 220,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                cursor: 'pointer',
                position: 'relative',
                textAlign: 'center',
                fontFamily: 'inherit',
              }}
            >
              {badge && (
                <div
                  style={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    background: C.orange,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 500,
                    padding: '3px 9px',
                    borderRadius: 20,
                  }}
                >
                  {badge.text}
                </div>
              )}
              {/* Icon and text as one centred block — see HubCard. */}
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: RADIUS.md,
                  background: C.amberLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon size={31} color={C.orange} stroke={2} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: 21, fontWeight: 500, color: C.textDark, textAlign: 'center' }}>
                  {tr(`home.tile.${key}`)}
                </div>
                <div style={{ fontSize: 13.5, fontStyle: 'italic', color: subtitleColor, marginTop: 4, lineHeight: 1.4, textAlign: 'center' }}>{subtitle}</div>
              </div>
            </button>
          ))}
        </div>

        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <button
            type="button"
            onClick={openVoice}
            aria-label="Voice"
            style={{
              position: 'relative',
              width: 78,
              height: 78,
              borderRadius: '50%',
              background: C.orange,
              border: `4px solid ${C.bg}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(196,96,29,0.4)',
              padding: 0,
            }}
          >
            <MicIcon size={34} color="#fff" />
            {voiceActive && (
              // green dot = a voice conversation is still alive (minimized)
              <span
                aria-label="Conversation in progress"
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: C.green,
                  border: `3px solid ${C.bg}`,
                }}
              />
            )}
          </button>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 26, fontSize: 13, color: C.textFaint }}>
        {tr('home.askHint')}
      </div>

      {/* Read-only "value created" strip — what Floxir has caught for you. Each
          stat is hidden when it's zero/unavailable rather than showing a fake 0. */}
      {(caughtAmount > 0 || billsChecked > 0 || runningLow > 0) && (
        <div style={{ maxWidth: 640, margin: '30px auto 0' }}>
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: RADIUS.lg,
              padding: '16px 22px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
              <IconShieldCheck size={16} color={C.orange} stroke={2} />
              <span
                style={{
                  fontSize: 11,
                  color: C.textFaint,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                }}
              >
                {tr('home.value.title')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              {caughtAmount > 0 && (
                <div>
                  <div style={{ fontSize: 24, fontWeight: 500, color: C.orange, lineHeight: 1 }}>
                    {fmtMoney(caughtAmount)}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                    {tr('home.value.caught')}
                  </div>
                </div>
              )}
              {billsChecked > 0 && (
                <div>
                  <div style={{ fontSize: 24, fontWeight: 500, color: C.textDark, lineHeight: 1 }}>
                    {billsChecked}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                    {tr(billsChecked === 1 ? 'home.value.billChecked' : 'home.value.billsChecked')}
                  </div>
                </div>
              )}
              {runningLow > 0 && (
                <div>
                  <div style={{ fontSize: 24, fontWeight: 500, color: C.red, lineHeight: 1 }}>
                    {runningLow}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                    {tr(runningLow === 1 ? 'home.value.lowOne' : 'home.value.lowMany')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── language switcher (Home) ────────────────────────────────────────────────
// IT / UK flags. The chosen language applies APP-WIDE (it sets the shared
// LanguageProvider state, which is persisted), not just on Home. The active
// flag is highlighted; missing Italian strings fall back to English.

function LangSwitcher({ lang, setLang }) {
  const tr = useT();
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <FlagButton active={lang === 'it'} onClick={() => setLang('it')} label={tr('lang.it')} Flag={ItFlag} />
      <FlagButton active={lang === 'en'} onClick={() => setLang('en')} label={tr('lang.en')} Flag={GbFlag} />
    </div>
  );
}

function FlagButton({ active, onClick, label, Flag }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      style={{
        width: 34,
        height: 26,
        padding: 0,
        borderRadius: 6,
        border: `2px solid ${active ? C.orange : C.border}`,
        background: C.card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        opacity: active ? 1 : 0.5,
        boxShadow: active ? '0 1px 4px rgba(196,96,29,0.25)' : 'none',
        overflow: 'hidden',
      }}
    >
      <Flag />
    </button>
  );
}

function ItFlag() {
  return (
    <svg width="22" height="15" viewBox="0 0 3 2" style={{ borderRadius: 2, display: 'block' }} aria-hidden="true">
      <rect width="1" height="2" x="0" fill="#009246" />
      <rect width="1" height="2" x="1" fill="#fff" />
      <rect width="1" height="2" x="2" fill="#CE2B37" />
    </svg>
  );
}

function GbFlag() {
  return (
    <svg width="22" height="15" viewBox="0 0 60 40" style={{ borderRadius: 2, display: 'block' }} aria-hidden="true">
      <rect width="60" height="40" fill="#012169" />
      <g stroke="#fff" strokeWidth="6">
        <line x1="0" y1="0" x2="60" y2="40" />
        <line x1="60" y1="0" x2="0" y2="40" />
      </g>
      <g stroke="#C8102E" strokeWidth="2.5">
        <line x1="0" y1="0" x2="60" y2="40" />
        <line x1="60" y1="0" x2="0" y2="40" />
      </g>
      <rect x="25" width="10" height="40" fill="#fff" />
      <rect y="15" width="60" height="10" fill="#fff" />
      <rect x="27" width="6" height="40" fill="#C8102E" />
      <rect y="17" width="60" height="6" fill="#C8102E" />
    </svg>
  );
}
